import { Camera, Loader2, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';

import { iniciaisDe } from '@/shared/components/Avatar';
import { RecorteDeImagemDialogo } from '@/shared/components/RecorteDeImagemDialogo';
import { cn } from '@/shared/lib/cn';

const TAMANHOS = {
  md: 'w-24 h-24 text-2xl',
  lg: 'w-32 h-32 text-3xl',
} as const;

/**
 * Avatar clicável para foto de perfil — sem botão embaixo, sem exigir que a
 * pessoa já mande a imagem no tamanho certo.
 *
 * O círculo inteiro é o campo: clicar nele abre o seletor de arquivo, que
 * dispara direto o recorte (`RecorteDeImagemDialogo`) — o servidor só recebe o
 * resultado já quadrado. Sem foto, mostra a inicial do nome (mesmo padrão de
 * `Avatar`), nunca um espaço em branco.
 */
export function CampoDeFoto({
  nome,
  urlAtual,
  enviando,
  aoEnviar,
  aoRemover,
  tamanho = 'md',
}: {
  nome?: string | null;
  urlAtual: string | null;
  enviando: boolean;
  aoEnviar: (arquivo: File) => void;
  /** Ausente = campo só troca, não oferece remover. */
  aoRemover?: () => void;
  tamanho?: keyof typeof TAMANHOS;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [paraRecortar, setParaRecortar] = useState<File | null>(null);

  function aoEscolherArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    if (arquivo) setParaRecortar(arquivo);
    // Zerar permite escolher o MESMO arquivo de novo depois de cancelar o recorte.
    e.target.value = '';
  }

  function fecharRecorte() {
    setParaRecortar(null);
  }

  function aoConfirmarRecorte(arquivoRecortado: File) {
    aoEnviar(arquivoRecortado);
    fecharRecorte();
  }

  return (
    <div className="relative inline-block group">
      <button
        type="button"
        onClick={() => entrada.current?.click()}
        disabled={enviando}
        aria-label={urlAtual ? 'Trocar foto' : 'Enviar foto'}
        className={cn(
          TAMANHOS[tamanho],
          'relative rounded-full overflow-hidden flex items-center justify-center',
          'bg-brand text-brand-foreground font-semibold uppercase',
          'ring-2 ring-brand/20 hover:ring-brand/40 transition disabled:cursor-not-allowed',
        )}
      >
        {urlAtual && <img src={urlAtual} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        {!urlAtual && iniciaisDe(nome, 2)}

        <span
          className={cn(
            'absolute inset-0 flex items-center justify-center transition-colors',
            enviando ? 'bg-black/40' : 'bg-black/0 group-hover:bg-black/40',
          )}
        >
          {enviando ? (
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          ) : (
            <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </span>
      </button>

      {aoRemover && urlAtual && !enviando && (
        <button
          type="button"
          onClick={aoRemover}
          aria-label="Remover foto"
          className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-md hover:bg-destructive/90"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}

      <input
        ref={entrada}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        aria-label="Arquivo de foto"
        onChange={aoEscolherArquivo}
      />

      {paraRecortar && (
        <RecorteDeImagemDialogo
          aberto
          aoFechar={fecharRecorte}
          arquivo={paraRecortar}
          proporcao={1}
          larguraSaida={500}
          // JPEG mesmo quando a origem é PNG: aqui é foto de rosto, não
          // ícone com transparência, e um PNG sem perdas de 500×500 é gordo à toa.
          mime="image/jpeg"
          redonda
          assunto="foto"
          enviando={enviando}
          aoConfirmar={aoConfirmarRecorte}
        />
      )}
    </div>
  );
}
